import type {
  AMLEventType,
  AccValEventType,
  Currency,
  LifetimeLimitAckType
} from '@swf/core/enums';
import { Site } from '@swf/core/enums';
import type { StatusCode } from '@swf/core/enums/statusCode';

import type { PendingLimits } from './PendingLimits';
import type { ErrorCollection } from './Response';

/* eslint-disable no-bitwise */
export enum UserPrivilege3 {
  MailListPoker = 1 << 0,
  MailListCasino = 1 << 1,
  MailListSports = 1 << 2,
  SportLiveAlert = 1 << 30,
  WithdrawalCancellation = 1 << 13,
  NoWithdraw = 1 << 7,
  NoSportsPlay = 1 << 4
}

export enum UserPrivilege2 {
  SuspendRM = 1 << (47 - 32),
  CannotSendTransfer = 1 << 3
}

export enum UserPrivilege {
  SelfExcluded = 1 << 26,
  PrivPlay = 1 << 0,
  PrivChat = 1 << 1
}

export enum UserFlag {
  RealMoneyOk = 1 << 3,
  AgeVerified = 1 << 12,
  AuthVerified = 1 << 20,
  RSARequired = 1 << 26,
  AccountPINRequired = 1 << 29
}

export enum UserFlag2 {
  ConvertBuyIn = 1 << 4,
  ConvertWinnings = 1 << 5,
  NoLoginEmailNotification = 1 << 8,
  BannedByRegulator = 1 << 28,
  AccountPINGenerated = 1 << 0,
  RSAActivated = 1 << 1,
  PendingFunds = 1 << 20
}

export enum UserFlag3 {
  OneTimePINRequired = 1 << 24,
  AMLVerified = 1 << (35 - 32),
  AMLDocsInProgress = 1 << (36 - 32),
  AMLSuspended = 1 << (37 - 32),
  AMLDepositThresholdReached = 1 << (38 - 32),
  AMLShowVerifiedModal = 1 << (40 - 32),
  KYCRequired = 1 << (46 - 32),
  VerifiedForMarketing = 1 << (47 - 32),
  VulnerablePlayer = 1 << (50 - 32)
}

// TODO: EmailFlags should be merged with UserFlags above
export enum EmailFlag {
  Verified = 1 << 0,
  Bounced = 1 << 6,
  NotAuthorized = 1 << (51 - 32),
  AlwaysValidated = 1 << (50 - 32)
}

export enum License {
  COM = 0x00000000,
  IT = 0x00000008,
  FR = 0x00000010,
  EE = 0x00000020,
  BE = 0x00000040,
  ES = 0x00000080,
  DK = 0x00000100,
  EU = 0x00000200,
  USNJ = 0x00000800,
  UK = 0x00001000,
  BG = 0x00002000,
  DESH = 0x00004000,
  RO = 0x00010000,
  PT = 0x00020000,
  CZ = 0x00040000,
  IN = 0x00080000,
  ASIA = 0x00100000,
  RU = 0x00200000,
  USPA = 0x00400000,
  SE = 0x00800000,
  CH = 0x01000000,
  USMI = 0x01000004,
  NL = 0x01000005,
  CAON = 0x01000006,
  GR = 0x01000022,
  USCO = 0x1000002,
  DE = 0x1000003
}

export enum BanPopupType {
  NotBanned,
  Banned,
  Unbanned,
  RemainBanned = 4
}

export enum OddsFormat {
  Decimal = 1,
  Fractional,
  American
}

export enum TemporaryAccountType {
  None = 0,
  AuthenticationPeriod = 2,
  Suspended = 3
}

export enum PlayerValPopupType {
  None,
  Blacklisted,
  DocVerificationRequired,
  RmActivityRestricted,
  AccountClosed
}
export interface AccountCurrency {
  currency: Currency;
  chips: number;
  tMoney: number;
}

export interface Flag {
  hi: number;
  lo: number;
}

export interface EmailValidationFlags {
  verified: boolean;
  bounced: boolean;
  notAuthorized: boolean;
  alwaysValidated: boolean;
}

export interface Response {
  success: boolean;
  message?: string;
  errors?: ErrorCollection;
  statusCode?: StatusCode;
  messages?: { msg: { error: string } };
}

export interface AccountLocales {
  emLocale: number;
  emLocale2: number;
  emLocale3: number;
}

export type AccountSummaryCount = Record<string, number>;

export interface AccountSummary {
  buyInCount?: AccountSummaryCount;
  winLossCount?: AccountSummaryCount;
  depositCount?: AccountSummaryCount;
  cashOutCount?: AccountSummaryCount;
  winningsCount?: AccountSummaryCount;
  lossesCount?: AccountSummaryCount;
  type?: TemporaryAccountType;
  days?: number;
}

export interface AccountInfoLastLoginDetails {
  lastSession: boolean;
  reasonOfLogout: string;
  lastLogoutIp: string;
  lastLoginTime: number;
  lastLogoutTime: number;
}

export interface AccountInfoWinLossData {
  winLoss: number;
  currency: Currency;
  prevLoginTime: number;
}

export interface LoginLimitInfo {
  loginTotal?: number;
  loginTotalLimit?: number;
  loginTimeTotalSec?: number;
  loginTimeLimitMin?: number;
  userSessionStartTime?: number;
}

export interface AccountInfo {
  name?: string;
  addr1?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  phone?: string;
}

export interface OccupationInfo {
  occupation: string;
  occupationId?: string;
  companyName?: string;
  companyPhone?: string;
  companyAddress?: string;
  sourceOfFunds?: string;
  sourceOfWealth?: string;
  pepHioSelection?: string;
  pepHioName?: string;
  relationshipToPepHio?: string;
  playerIsNot3Party?: boolean;
  playerIsNotPEP?: boolean;
}

export interface PlaceOfBirthInfo {
  countryOfBirth: string;
  stateOfBirth: string;
  cityOfBirth: string;
}

export interface Account {
  accountSummary: AccountSummary | null;
  accValEventType?: AccValEventType;
  addr1: string;
  addr2: string;
  amlDaysLeft: number;
  amlDepositThresholdReachedTimestamp: number;
  amlEventType: AMLEventType;
  authLoginCode: number;
  autoAcceptPriceChanges: boolean;
  balances: AccountCurrency[];
  banPopUp: BanPopupType;
  buildingNumber: string;
  city: string;
  country: string;
  dataIntegrityCheckFailed: boolean;
  dateOfBirth: number;
  defaultCurrencyForCountry: Currency;
  depositConfirmLimits: PendingLimits[] | null;
  displayValueProposition: boolean;
  district?: string;
  dormantSuspended: boolean;
  dob: string;
  docExpiryDate: string;
  docIssueDate: string;
  docIssuePlace: string;
  docIssuingAuthority: number;
  docNumber: string;
  docType: number;
  docsDaysLeft: number;
  avcDaysLeft: number;
  email: string;
  esCitizenOrResident: boolean;
  eulaRequired: boolean;
  eulaSigned?: number;
  euSilentMigration?: boolean;
  firstName: string;
  flags2: Flag;
  flags3: Flag;
  flags: number;
  forcedCashout: boolean;
  fppAmount: number;
  fullName: string;
  gameConfirmLimits: PendingLimits[] | null;
  gamingAccountNumber?: string;
  gender: string | null;
  imageId: number;
  info: {
    casinoRealityCheck?: number | null;
    lastLoginDetails?: AccountInfoLastLoginDetails;
    sessionWinLossData?: AccountInfoWinLossData;
    loginLimitInfo?: LoginLimitInfo;
    confirmAccountDetails?: boolean;
  };
  infoSelfExclMsg: string | null;
  lastName: string;
  licenseId: License;
  lifetimeLimitAckAction: boolean;
  lifetimeLimitAckLifetimeLimit: number;
  lifetimeLimitAckTypeOfPopUp: LifetimeLimitAckType;
  middleName: string;
  migrationFlag: number;
  migrationMsg: null;
  migrationTarget: number;
  mobilePhone: string;
  monthlyNetDepositLimit?: number;
  netDepositLimitCurrency?: Currency;
  occupationInfo: OccupationInfo;
  oddsFormat: Nullable<OddsFormat>;
  orientationNumber?: string;
  partOfMunicipality?: string;
  passwordWeak: boolean;
  phone: string;
  placeOfBirth?: string;
  placeOfBirthInfo: Nullable<PlaceOfBirthInfo>;
  playChips: number;
  playerValPopupType?: PlayerValPopupType;
  playerVerification: boolean;
  preventLogin: boolean;
  privileges2: Flag;
  privileges3: Flag;
  privileges: number;
  region?: string;
  registered?: number;
  reloader?: boolean;
  selfExcluded: boolean;
  selfExclusionExpirationDate: string | null;
  selfExclusionExpired: boolean;
  showBalance?: boolean;
  showUkaYoungUserDialog?: boolean;
  showReinstateDialog?: boolean;
  signEulaOptions: number;
  state: string;
  streetName: string;
  tempAccount: boolean;
  timestamp: number;
  timezone: number;
  totalBalance: number;
  totalBalanceInUsd: number;
  totalTMoney: number;
  totalTMoneyCurrency: Currency;
  userId: string;
  userIntId: number;
  verifyWithdrawalMethod: boolean;
  webClientDialog: string;
  webClientDialogFlag: number;
  zipCode: string;
  netDepositLimitGroup?: number;
  lugasPlayerStatus?: LugasPlayerStatusState;
}

export const depositLimitExtendedSites = [
  Site.COM,
  Site.DE,
  Site.DESH,
  Site.DK,
  Site.ES,
  Site.EU,
  Site.GR,
  Site.NL,
  Site.PT,
  Site.RO,
  Site.SE,
  Site.UK,
  Site.USCO,
  Site.USMI,
  Site.USNJ,
  Site.USPA,
  Site.CAON
];

export type AccountPayload =
  | (
      | { occupationInfo: OccupationInfo }
      | { accountInfo: AccountInfo }
      | { placeOfBirthInfo: PlaceOfBirthInfo }
      | { playerIsNotPEP: boolean; playerIsNot3Party: boolean; realMoneyFlag: boolean }
      | { realMoneyFlag: boolean }
      | {
          mailListPoker: boolean;
          mailListCasino: boolean;
          mailListSports: boolean;
          liveAlertSport: boolean;
        }
      | { email: Account['email'] }
      | { timezone?: string; timezoneName?: string }
      | { convertBuyIns: boolean; convertWinnings: boolean }
      | { mobilePhone?: Account['mobilePhone']; phone?: Account['phone'] }
      | { dob: string }
      | { autoAcceptPriceChanges: boolean }
      | { firstName: string; lastName: string }
      | { oddsFormat: OddsFormat }
      | { middleName: string }
      | { citizenship: string; nationalDocumentNumber?: string }
      | {
          addr1?: string;
          streetAddress?: string;
          city?: string;
          state?: string;
          zip: string;
          zipCode?: string;
          country: string;
          buildingNumber?: string;
          streetName?: string;
        }
      | {
          docExpiryDate: string;
          docIssueDate: string;
          docIssuePlace: string;
          docIssuingAuthority: number;
          docNumber: string;
          docType: number;
        }
      | { nationalDocumentNumber: string }
      | {
          question1: number;
          question2: number;
          answer1: string;
          answer2: string;
        }
    ) & { timeout?: number; fetchAccount?: boolean };

export enum UpdateAccountDetailsAction {
  Cancel,
  Confirm,
  Update
}

export enum LugasPlayerStatus {
  UNDEFINED = 'UNDEFINED',
  ACTIVE = 'ACTIVE',
  PENDING_LONG_SESSION = 'PENDING_LONG_SESSION'
}

export interface LugasPlayerStatusState {
  status: LugasPlayerStatus;
  lastAcknowledgementDate?: number;
}
